import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { fmtError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Coins as CoinsIcon, Check, ShoppingBag, Lock } from "lucide-react";

const CATEGORY_META = {
  name_style:       { key: "shop.catNameStyle",     fallback: "Display name styles" },
  avatar_decoration:{ key: "shop.catAvatarDeco",    fallback: "Avatar decorations" },
  banner:           { key: "shop.catBanner",        fallback: "Profile banners" },
  profile_effect:   { key: "shop.catEffect",        fallback: "Profile effects" },
  name_badge:       { key: "shop.catBadge",         fallback: "Name badges" },
  profile_theme:    { key: "shop.catTheme",         fallback: "Profile themes" },
};

const RARITY_COLOR = {
  common:    "#9ca3af",
  uncommon:  "#10b981",
  rare:      "#3b82f6",
  epic:      "#a855f7",
  legendary: "#f59e0b",
};

function ItemPreview({ item }) {
  const d = item.data || {};
  const { category, name } = item;
  if (category === "name_style") {
    return (
      <div
        className={`font-display font-black text-2xl name-gradient ${d.animate ? "animated" : ""}`}
        style={{ backgroundImage: d.gradient }}
      >
        Sample Name
      </div>
    );
  }
  if (category === "avatar_decoration") {
    const ringBg = d.gradient || d.color || "#22d3ee";
    return (
      <div
        className="avatar-ring shadow-nb-sm"
        style={{
          background: ringBg,
          padding: `${d.thickness || 4}px`,
          boxShadow: d.glow ? `0 0 18px ${d.color || "#22d3ee"}` : undefined,
        }}
      >
        <div className="w-16 h-16 rounded-full bg-[#FFF0D4] border-2 border-[#121212] flex items-center justify-center font-display font-black text-2xl">T</div>
      </div>
    );
  }
  if (category === "banner") {
    return <div className="w-full h-20 rounded-xl border-2 border-[#121212]" style={{ background: d.background }} />;
  }
  if (category === "profile_effect") {
    const cls = { sparkle: "fx-sparkle", pulse: "fx-pulse", confetti: "fx-confetti", aurora: "fx-aurora" }[d.effect] || "";
    return (
      <div className={`w-full h-20 rounded-xl border-2 border-[#121212] bg-[#FFF0D4] ${cls} flex items-center justify-center font-bold`}>
        {name}
      </div>
    );
  }
  if (category === "name_badge") {
    return (
      <span className="name-badge" style={{ background: d.color + "22", borderColor: d.color }}>
        <span>{d.icon}</span> {d.label}
      </span>
    );
  }
  if (category === "profile_theme") {
    return (
      <div className="w-full h-20 rounded-xl border-2 border-[#121212] p-3 flex items-center justify-center" style={{ background: d.bg, color: d.text }}>
        <span className="font-display font-black">Aa</span>
      </div>
    );
  }
  return null;
}

export default function ProfileShop() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const { user, refresh } = useAuth();
  const [catalog, setCatalog] = useState([]);
  const [categories, setCategories] = useState([]);
  const [me, setMe] = useState({ inventory: [], equipped: {}, balance: 0 });
  const [activeCat, setActiveCat] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    try {
      const [c, m] = await Promise.all([
        api.get("/shop/catalog"),
        api.get("/shop/me"),
      ]);
      setCatalog(c.data.items || []);
      setCategories(c.data.categories || []);
      setActiveCat((cur) => cur || (c.data.categories || [])[0]);
      setMe(m.data);
    } catch (e) {
      toast.error(fmtError(e.response?.data?.detail) || e.message);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const items = useMemo(() => catalog.filter((x) => x.category === activeCat), [catalog, activeCat]);
  const owned = (id) => me.inventory?.includes(id);
  const equipped = (cat, id) => me.equipped?.[cat] === id;

  const buy = async (item) => {
    if (busy) return;
    setBusy(item.id);
    try {
      await api.post("/shop/buy", { item_id: item.id });
      toast.success(t("shop.purchased", { name: item.name, defaultValue: `Unlocked: ${item.name}` }));
      await load();
      await refresh();
    } catch (e) {
      toast.error(fmtError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(null);
    }
  };

  const equip = async (item) => {
    if (busy) return;
    setBusy(item.id);
    try {
      const isOn = equipped(item.category, item.id);
      await api.post("/shop/equip", { category: item.category, item_id: isOn ? null : item.id });
      toast.success(isOn ? t("shop.unequipped", { defaultValue: "Removed" }) : t("shop.equipped", { defaultValue: "Equipped!" }));
      await load();
      await refresh();
    } catch (e) {
      toast.error(fmtError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-6 pb-32" data-testid="profile-shop-page">
      <button onClick={() => nav(-1)} className="btn-nb mb-5" data-testid="shop-back-btn">
        <ArrowLeft className="w-4 h-4" strokeWidth={3} /> {t("common.back", "Back")}
      </button>

      <div className="card-nb p-6 mb-5 bg-gradient-to-br from-[#FFF1D6] to-[#FFE08A]">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h1 className="font-display font-black text-3xl flex items-center gap-2">
            <ShoppingBag className="w-7 h-7" strokeWidth={2.5} /> {t("shop.title", "Profile Shop")}
          </h1>
          <div className="tag-nb bg-white flex items-center gap-1" data-testid="shop-balance">
            <CoinsIcon className="w-4 h-4 text-[#F9C349]" fill="#F9C349" />
            <span className="stat-num">{me.balance ?? user?.coins ?? 0}</span>
          </div>
        </div>
        <p className="text-sm text-[#4A3000] mt-2">{t("shop.subtitle", "Personalise your profile with premium customizations purchased with T-Coins.")}</p>
      </div>

      {/* Category tabs */}
      <div className="flex gap-2 overflow-x-auto pb-3 mb-4 -mx-2 px-2" data-testid="shop-tabs">
        {categories.map((cat) => (
          <button
            key={cat}
            onClick={() => setActiveCat(cat)}
            data-testid={`shop-tab-${cat}`}
            className={`btn-nb whitespace-nowrap ${activeCat === cat ? "btn-nb-info" : ""}`}
          >
            {t(CATEGORY_META[cat]?.key, CATEGORY_META[cat]?.fallback || cat)}
          </button>
        ))}
      </div>

      {/* Item grid */}
      <div className="grid sm:grid-cols-2 gap-4" data-testid="shop-items">
        {items.map((item) => {
          const isOwned = owned(item.id);
          const isEquipped = equipped(item.category, item.id);
          const canAfford = (me.balance ?? 0) >= (item.price_coins || 0);
          return (
            <div
              key={item.id}
              data-testid={`shop-item-${item.id}`}
              className="card-nb p-4 bg-white flex flex-col gap-3"
            >
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="font-display font-black text-lg leading-tight">{item.name}</div>
                  <div className="text-[10px] uppercase tracking-widest font-bold mt-0.5" style={{ color: RARITY_COLOR[item.rarity] || "#666" }}>
                    {item.rarity}
                  </div>
                </div>
                <div className="tag-nb bg-[#FFF1D6] flex items-center gap-1 shrink-0">
                  <CoinsIcon className="w-3.5 h-3.5 text-[#F9C349]" fill="#F9C349" />
                  <span className="stat-num text-sm">{item.price_coins}</span>
                </div>
              </div>

              <div className="flex items-center justify-center min-h-[80px]">
                <ItemPreview item={item} />
              </div>

              <div className="flex gap-2">
                {isOwned ? (
                  <button
                    onClick={() => equip(item)}
                    disabled={busy === item.id}
                    data-testid={`shop-equip-${item.id}`}
                    className={`btn-nb flex-1 ${isEquipped ? "btn-nb-warn" : "btn-nb-info"}`}
                  >
                    {isEquipped ? (
                      <><Check className="w-4 h-4" strokeWidth={3} /> {t("shop.unequip", "Equipped")}</>
                    ) : (
                      <>{t("shop.equip", "Equip")}</>
                    )}
                  </button>
                ) : (
                  <button
                    onClick={() => buy(item)}
                    disabled={busy === item.id || !canAfford}
                    data-testid={`shop-buy-${item.id}`}
                    className={`btn-nb flex-1 ${canAfford ? "" : "opacity-60"}`}
                  >
                    {!canAfford && <Lock className="w-4 h-4" strokeWidth={3} />}
                    {canAfford ? t("shop.unlock", "Unlock") : t("shop.notEnough", "Need more coins")}
                  </button>
                )}
              </div>
            </div>
          );
        })}
        {items.length === 0 && (
          <div className="col-span-full text-center text-sm text-[#4A4A4A] py-10">
            {t("shop.empty", "No items in this category yet.")}
          </div>
        )}
      </div>
    </div>
  );
}
